import { inject, Injectable } from '@angular/core';
import { ContextService } from 'core/services/context.service';
import { environment } from 'environment/environment';
import { initialize, LDClient, LDContext, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, combineLatest, fromEvent, map, Observable, of, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  private client?: LDClient;
  private destroy$ = new Subject<void>();
  private contextService = inject(ContextService);
  private initialized$ = new BehaviorSubject<boolean>(false);

  public get isInitialized$(): Observable<boolean> {
    return this.initialized$.asObservable();
  }

  public close(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.client?.close().then();
    this.initialized$.next(false);
  }

  public init: () => Promise<void> = () => {
    return new Promise<void>((resolve) => {
      // 1) kick off an anonymous client
      const anonContext: LDContext = { key: 'anonymous', anonymous: true };
      this.client = initialize(environment.launchDarklyClientId, anonContext);

      // 2) wait for the flags to arrive
      this.client.on('ready', () => {
        const flags = this.client!.allFlags();
        this.initializeForSiteAndClient();
        console.log('[LaunchDarkly] Initial flags:', flags);
        this.contextService.featureFlags.next(flags);
        resolve();
      });
    });
  };

  private initializeForSiteAndClient(): void {
    combineLatest([this.contextService.clientId, this.contextService.siteId, this.contextService.userInfo])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([clientId, siteId, userInfo]) => {
        if (!clientId || !siteId || !userInfo) {
          return;
        }
        const context: LDContext = {
          kind: 'user',
          key: userInfo.authId,
          clientId,
          siteId,
        };

        if (!this.client) {
          this.client = initialize(environment.launchDarklyClientId, context);

          this.client.on('ready', () => {
            const flags = this.client!.allFlags();
            this.contextService.featureFlags.next(flags);
          });

          this.client.on('change', (flags: Record<string, unknown>) => {
            this.populateFlags(flags);
          });
        } else {
          this.client.identify(context, undefined, (_: Error | null, flags: LDFlagSet | null) => {
            if (flags) {
              this.populateFlags(flags);
            } else {
              console.log('[LaunchDarkly] Identify callback returned no flags');
            }
          });
        }
      });
  }

  public isFeatureFlagEnabledForSiteAndClient(
    flagName: string,
    siteId?: string,
    clientId?: string,
  ): Observable<boolean> {
    const userInfo = this.contextService.userInfo.getValue();
    if (!clientId || !siteId || !userInfo) {
      return of(false);
    }

    const context: LDContext = {
      kind: 'user',
      key: userInfo.authId,
      clientId,
      siteId,
    };

    const client = initialize(environment.launchDarklyClientId, context);
    return fromEvent(client, 'ready').pipe(
      takeUntil(this.destroy$),
      map(() => client.variation(flagName, false)),
    );
  }

  private populateFlags(flags: LDFlagSet): void {
    const existing = this.contextService.featureFlags.getValue() ?? {};
    const updated = { ...existing, ...flags };
    this.contextService.featureFlags.next(updated);
    this.initialized$.next(true);
  }
}
